import React, { useEffect, useMemo, useState } from "react";

import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

// components
import TextInput from "../../../../../../components/Inputs/InputText";
import SelectInput from "../../../../../../components/Inputs/SelectInput";
import Typography from "../../../../../../components/Text";

// store
import useStore from "../../../../store";

// hooks
import { useCohorts, usePageSettings, useUpdatePageSettings } from "../../../../../../api/hooks";

// config
import Config from "../../../../Config";
import { IPageSettings } from "../../../../schema";
// styles
import css from "./QuestionsTable.module.scss";

const { DEFAULT_COHORT_SELECTION, DEFAULT_FACTOR_SELECTION } = Config.QUESTIONS.TABLE;

type QuestionsTableToolbarProps = {
	onSearch: (searchTerm: string) => void;
	additionalFeedbackQuestionEnabled: boolean;
	customFactorsEnabled?: boolean;
	customFactorsData?: { name: string }[];
};

const QuestionsTableToolbar = ({
	onSearch,
	additionalFeedbackQuestionEnabled,
	customFactorsEnabled,
	customFactorsData,
}: QuestionsTableToolbarProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const { isLoading: isCohortsLoading, data: cohortData } = useCohorts();

	const cohortOptions = [{ _id: "all", name: "All Employees" }, ...(cohortData?.cohorts || [])];

	// cohort lives in the store so the table and AI summary panel see the
	// selection synchronously, instead of waiting on the page-settings round-trip
	const [storeCohort, setSelectedCohort] = useStore((state) => [state.questions.cohort, state.questions.setCohort]);
	const cohort = storeCohort ?? DEFAULT_COHORT_SELECTION;
	const [factor, setSelectedFactor] = useState<string>(DEFAULT_FACTOR_SELECTION);

	const pageSettingsOnSuccess = (onSuccessData: IPageSettings) => {
		const newSelectedCohort = onSuccessData?.questions?.allQuestionsTable?.cohort;
		const newSelectedFactor = onSuccessData?.questions?.allQuestionsTable?.factor;
		const cohortExists = cohortOptions.find((c) => c._id === newSelectedCohort);
		// hydrate only — a non-null store value is a newer user selection
		if (storeCohort === null && newSelectedCohort && cohortExists) {
			setSelectedCohort(newSelectedCohort);
		}
		if (newSelectedFactor && newSelectedFactor !== factor) {
			setSelectedFactor(newSelectedFactor);
		}
	};

	const updatePageSettings = useUpdatePageSettings(pageSettingsOnSuccess).mutate;
	// state for selected cohorts
	const {
		data: pageSettingsData,
		refetch: refetchPageSettings,
		isLoading: isPageSettingsLoading,
		isSuccess,
	} = usePageSettings();

	useEffect(() => {
		if (isSuccess) {
			pageSettingsOnSuccess(pageSettingsData);
		}
	}, [isPageSettingsLoading]);

	// refetch page settings when cohorts have loaded
	useEffect(() => {
		if (!isCohortsLoading) {
			refetchPageSettings();
		}
	}, [isCohortsLoading]);

	const onSearchChanged = (searchTerm) => {
		setSearchTerm(searchTerm);
	};

	const onFactorChanged = ({ value: newFactor }) => {
		if (newFactor !== factor) {
			setSelectedFactor(newFactor);
			updatePageSettings({ settings_path: "questions.allQuestionsTable.factor", settings_value: newFactor });
		}
	};

	const onCohortChanged = ({ value: selection }) => {
		if (selection !== cohort) {
			setSelectedCohort(selection);
			updatePageSettings({ settings_path: "questions.allQuestionsTable.cohort", settings_value: selection });
		}
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			onSearch(searchTerm);
		}, 500);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	const dropdownCohorts = [
		{ label: "All Employees", value: "all" },
		...(cohortData?.cohorts.map((cohort) => ({
			label: cohort.name,
			value: cohort._id,
		})) || []),
	];

	const dropdownFactors = useMemo(() => {
		const options = [
			...Config.FACTORS.FACTORS,
			...(customFactorsEnabled && customFactorsData?.length
				? [...customFactorsData.map((f) => f.name), "Unassigned"]
				: ["Custom"]),
			"eNPS",
			...(additionalFeedbackQuestionEnabled ? ["Other"] : []),
		];
		return [
			{ label: "All Factors", value: "All Factors" },
			...options.sort((a, b) => a.localeCompare(b)).map((factor) => ({ label: factor, value: factor })),
		];
	}, [customFactorsEnabled, customFactorsData, additionalFeedbackQuestionEnabled]);

	// Reset stale factor filter (e.g. after a custom factor is renamed or deleted)
	useEffect(() => {
		if (factor !== DEFAULT_FACTOR_SELECTION && !dropdownFactors.some((d) => d.value === factor)) {
			setSelectedFactor(DEFAULT_FACTOR_SELECTION);
			updatePageSettings({
				settings_path: "questions.allQuestionsTable.factor",
				settings_value: DEFAULT_FACTOR_SELECTION,
			});
		}
	}, [factor, dropdownFactors]);

	return (
		<div className={css.toolbar}>
			<label htmlFor="cohort-filter">
				<Typography size="caption" weight="semi-bold">
					Filter by Cohort
				</Typography>
			</label>
			<div className={css.select}>
				<SelectInput
					size="small"
					inputId="cohort-filter"
					results={dropdownCohorts}
					onChange={onCohortChanged}
					value={cohort}
				/>
			</div>
			<label htmlFor="factor-filter">
				<Typography size="caption" weight="semi-bold">
					Filter by Factor
				</Typography>
			</label>
			<div className={css.select}>
				<SelectInput
					size="small"
					inputId="factor-filter"
					results={dropdownFactors}
					onChange={onFactorChanged}
					value={factor}
					isSearchable={false}
				/>
			</div>
			<div className={css.search}>
				<TextInput
					icon={faMagnifyingGlass}
					value={searchTerm}
					onChange={onSearchChanged}
					size="small"
					placeholder="Search questions..."
					ariaLabel="Search questions"
					clearable={true}
				/>
			</div>
		</div>
	);
};

export default QuestionsTableToolbar;
